import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ContentLoader,
  IconCheckmark,
  IconDisabled,
  List,
} from 'shared/components';
import { displayValue, formatDate } from 'shared/helpers';
import { fetchAccountLicenseUsers } from '../../../actions';
import '../../styles.scss';

const PAGE_SIZE = 20;

const LicenseUsersTab = ({ accountId }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [currentSort, setCurrentSort] = useState([
    { desc: true, id: 'created_at' },
  ]);
  const [retryToken, setRetryToken] = useState(0);

  const handleRowClick = (row) => {
    const userId = get(row, 'id');
    if (userId) {
      history.push(`/license_users/${userId}`);
    }
  };

  useEffect(() => {
    setPage(0);
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    let isStale = false;
    setLoading(true);
    setError(false);
    fetchAccountLicenseUsers(accountId, {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      orderBy: currentSort,
    })
      .then((res) => {
        const payload = get(res, 'data');
        const results = Array.isArray(payload)
          ? payload
          : get(payload, 'results', []);
        const totalCount = Array.isArray(payload)
          ? results.length
          : get(payload, 'count', results.length);
        if (!isStale) {
          setUsers(results);
          setCount(totalCount);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!isStale) {
          setError(true);
          setUsers([]);
          setCount(0);
          setLoading(false);
        }
      });
  }, [accountId, currentSort, page, retryToken]);

  const handleRetry = () => setRetryToken((token) => token + 1);

  const columns = [
    {
      Header: t('Created on'),
      accessor: 'created_at',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 150,
    },
    {
      Header: t('Email'),
      accessor: 'true_email',
      Cell: (cellData) => displayValue(cellData.value),
    },
    {
      Header: t('First Name'),
      accessor: 'first_name',
      Cell: (cellData) => displayValue(cellData.value),
    },
    {
      Header: t('Last Name'),
      accessor: 'last_name',
      Cell: (cellData) => displayValue(cellData.value),
    },
    {
      Header: t('Is Active'),
      accessor: 'is_active',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (cellData) =>
        cellData.value ? (
          <IconCheckmark color="#10ac84" height="14px" />
        ) : (
          <IconDisabled color="red" height="14px" />
        ),
      width: 110,
    },
  ];

  if (loading) {
    return <ContentLoader text={t('Loading license users')} />;
  }

  if (error) {
    return (
      <div className="CustomerAccount-error">
        <p>{t('Unable to load license users for this account')}</p>
        <Button theme="info" size="sm" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  if (!users.length) {
    return (
      <div className="CustomerAccount-empty">
        {t('No license users for this account')}
      </div>
    );
  }

  const pages = Math.ceil(count / PAGE_SIZE);

  return (
    <div className="CustomerAccount-list">
      <List
        data={users}
        columns={columns}
        page={page}
        pages={pages}
        manual
        minRows={users.length || 5}
        showPagination={pages > 1}
        loading={loading}
        onPageChange={(newPage) => setPage(newPage)}
        onSortedChange={(newSorted) => setCurrentSort(newSorted)}
        defaultSorted={currentSort}
        defaultPageSize={PAGE_SIZE}
        getTrProps={(state, rowInfo) => ({
          onClick: () => handleRowClick(rowInfo.original),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
};

LicenseUsersTab.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default LicenseUsersTab;
